import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { SocketContext } from '../Providers/socketProvider';
import { useParams } from 'react-router-dom';
import useTimer from '../Hooks/useTimer';
import { getEventData, getOptionsData } from '../Components/UpdatePlayerInfo';

const SocketWrapper = ({ component }) => {
  const socket = useContext(SocketContext);
  const { id } = useParams();

  const searchParams = new URLSearchParams(window.location.search);
  const screen = searchParams.get('screen');

  const [index, setIndex] = useState(0);
  const [name, setName] = useState('');
  const [tv, setTv] = useState(''); // Now used consistently
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [players, setPlayers] = useState([]);
  const [host, setHost] = useState(null);
  const [errorMessage, setErrorMessage] = useState({
    heading: 'this app uses essential cookies',
    copy: 'we may also use non-essential cookies with your consent.',
    close: false,
    button: { text: 'ALLOW', link: null },
  });
  const [lobbyId, setLobbyId] = useState(null);
  const [gameStatus, setGameStatus] = useState('not-started');
  const [timeLeft, setTimeLeft] = useState(0);
  const [startCountdown, setStartCountdown] = useState(false);
  const [currentNumbers, setCurrentNumbers] = useState([]);
  const [modalOpen, setModalOpen] = useState(true);
  const [currentLevels, setCurrentLevels] = useState([
    { playerNumber: 1, level: 0 },
    { playerNumber: 2, level: 0 },
    { playerNumber: 3, level: 0 },
  ]);
  const [stages, setStages] = useState([
    { playerNumber: 1, stage: 0 },
    { playerNumber: 2, stage: 0 },
    { playerNumber: 3, stage: 0 },
  ]);
  const [ranks, setRanks] = useState([
    { playerNumber: 1, rank: null, time: null },
    { playerNumber: 2, rank: null, time: null },
    { playerNumber: 3, rank: null, time: null },
  ]);

  const gameTimerRef = useRef();
  const gameStatusRef = useRef();
  gameStatusRef.current = gameStatus;
  const currentLevelsRef = useRef();
  currentLevelsRef.current = currentLevels;
  const stagesRef = useRef();
  stagesRef.current = stages;
  const ranksRef = useRef();
  ranksRef.current = ranks;
  const currentNumbersRef = useRef();
  currentNumbersRef.current = currentNumbers;

  const scoreTimer = useTimer();

  // Reset local state and notify server
  const resetState = useCallback(() => {
    setGameStatus('not-started');
    setName('');
    setTv(''); // Reset TV explicitly
    setPlayers([]);
    setHost(null);
    setLobbyId(null);
    setRanks([]);
    setCurrentLevels([]);
    setStages([]);
    setIndex(0);
    if (socket && lobbyId) {
      socket.emit('resetClientState', { lobbyId, type: 'tv', clientId: socket.id }); // Notify server
    }
  }, [socket, lobbyId]);

  useEffect(() => {
    if (!lobbyId) {
      setLobbyId(`${id}`);
    }
  }, [id, lobbyId]);

  const [optionData, setOptionData] = useState([]);
  const [eventData, setEventData] = useState([]);
  useEffect(() => {
    async function fetchEventData() {
      try {
        const eventData = await getEventData(id);
        const optionApiData = await getOptionsData();
        setEventData(eventData);
        setOptionData(optionApiData[0]);
      } catch (e) {
        console.log(e);
      }
    }
    if (gameStatus === 'not-started') {
      fetchEventData();
    }
  }, [gameStatus, id]);

  useEffect(() => {
    if (scoreTimer.elapsedTime > 120000) {
      if (screen === 'leaderboard') {
        setGameStatus('not-started');
        return;
      }
      scoreTimer.handlePause();
      const endGameRanks = ranks.map((rank) => {
        if (rank.rank === null) {
          return { playerNumber: rank.playerNumber, rank: 'lost', time: 9999999999 };
        }
        return rank;
      });
      setRanks(endGameRanks);
      socket.emit('allGamesCompleted', { lobbyId });
    }
  }, [lobbyId, ranks, scoreTimer, screen, socket]);

  const joinLobby = useCallback(
    (name = null, email = null, company = null, type = 'tv') => {
      setErrorMessage(null);
      const screenType = screen || 'game';
      if (lobbyId) {
        socket.emit('joinLobby', { name, lobbyId, type, screen: screenType, clientId: socket.id });
      }
    },
    [lobbyId, socket, screen]
  );

  const leaveLobby = useCallback(() => {
    if (lobbyId) {
      socket.emit('leaveLobby', { lobbyId, type: 'tv', clientId: socket.id });
      resetState(); // Reset state after leaving
    }
  }, [lobbyId, socket, resetState]);

  const removePlayer = (lobbyId, playerId) => {
    socket.emit('removePlayer', { lobbyId, playerId });
  };

  const updatePlayerstatus = (lobbyId, playerId, status) => {
    socket.emit('playerStatusUpdated', { lobbyId, playerId, status });
  };

  const startGame = (lobbyId) => {
    socket.emit('startGame', { lobbyId });
  };

  const incorrectAnswer = ({ lobbyId, currentLevel, stage }) => {
    socket.emit('incorrectAnswer', { lobbyId });
  };

  const resetGame = useCallback(() => {
    leaveLobby();
    resetState();
  }, [leaveLobby, resetState]);

  const gameincomplete = useCallback(() => {
    if (screen !== 'game') return;
    scoreTimer.handlePause();
    setGameStatus('complete');
    socket.emit('gameComplete', { lobbyId, playerId: socket.id, time: 9999999999 });
  }, [screen, scoreTimer, socket, lobbyId]);

  useEffect(() => {
    if (socket && lobbyId) {
      joinLobby(name, null, null, 'tv'); // Auto-join TV on connect
    }
  }, [socket, lobbyId, joinLobby, name]);

  useEffect(() => {
    if (!socket) return;

    socket.on('lobbyFull', () => {
      setErrorMessage({ heading: 'The lobby is full.', copy: null, button: { text: 'OK', link: '/' }, close: false });
      setModalOpen(true);
    });

    socket.on('playerJoined', ({ playerId, player, players, host }) => {
      if (screen === 'extra') {
        setGameStatus('in-progress-idle');
        return;
      }
      if (screen !== 'game') return;
      const playerInGame = players.some((p) => p.status === 'in-game');
      if (playerInGame) {
        setErrorMessage({
          heading: 'The game has already started.',
          copy: null,
          button: { text: 'OK', link: '/' },
          close: false,
        });
      } else {
        setPlayers(players);
        setHost(host);
        setGameStatus('lobby');
        if (player.id === socket.id) {
          setName(player.name);
        }
      }
    });

    socket.on('tvJoined', ({ players, tv }) => {
      console.log(`TV joined lobby ${lobbyId}:`, tv);
      setTv(tv);
      setPlayers(players);
      setGameStatus('not-started');
    });

    socket.on('playersUpdated', (players) => {
      if (screen !== 'game') return;
      setPlayers(players);
    });

    socket.on('startTimer', ({ duration }) => {
      const d = process.env.REACT_APP_GAMETIME || duration;
      if (screen !== 'game') {
        setGameStatus('in-progress-idle');
        return;
      }
      setTimeLeft(d);
      setGameStatus('in-progress');
      const playerStartingLevels = players.map((p) => ({ playerNumber: p.playerNumber, level: 0 }));
      const playerStartingStages = players.map((p) => ({ playerNumber: p.playerNumber, stage: 0 }));
      const playerStartingRanks = players.map((p) => ({ playerNumber: p.playerNumber, rank: null, time: null }));
      setCurrentLevels(playerStartingLevels);
      setStages(playerStartingStages);
      setRanks(playerStartingRanks);
      setStartCountdown(true);
    });

    socket.on('numbersGenerated', ({ sequences }) => {
      if (screen !== 'game') return;
      setCurrentNumbers(sequences);
    });

    socket.on('correctAnswer', ({ playerNumber, stage }) => {
      if (screen !== 'game') return;
      setStages((prev) => prev.map((s) => (s.playerNumber === playerNumber ? { ...s, stage: stage + 1 } : s)));
    });

    socket.on('incorrectAnswer', ({ playerNumber }) => {
      if (screen !== 'game') return;
      setStages((prev) => prev.map((s) => (s.playerNumber === playerNumber ? { ...s, stage: 0 } : s)));
    });

    socket.on('numbersUpdated', ({ currentLevel, numbers }) => {
      if (screen !== 'game') return;
      setStages((prev) => prev.map((s) => (s.playerNumber === numbers.playerNumber ? { ...s, stage: 0 } : s)));
      setCurrentLevels((prev) =>
        prev.map((l) => (l.playerNumber === numbers.playerNumber ? { ...l, level: currentLevel } : l))
      );
      setCurrentNumbers((prev) =>
        prev.map((n) => (n.playerNumber === numbers.playerNumber ? { ...n, numbers: numbers.numbers } : n))
      );
    });

    socket.on('gameCompleted', ({ rank, playerNumber, time }) => {
      if (screen === 'leaderboard') {
        setGameStatus('not-started');
        return;
      }
      if (screen !== 'game') return;
      const indexRanks = ranks.findIndex((s) => s.playerNumber === playerNumber);

      setRanks((prevRanks) => {
        const updatedRanks = [...prevRanks];
        updatedRanks[indexRanks] = {
          playerNumber: playerNumber,
          rank,
          time,
        };

        console.log(updatedRanks);

        if (
          updatedRanks
            .filter(
              (rank) => !(rank.rank === null && !players.some((player) => player.playerNumber === rank.playerNumber))
            )
            .every((r) => r.rank !== null)
        ) {
          gameTimerRef.current.pause();
          socket.emit('allGamesCompleted', { lobbyId });
          setTimeout(() => {
            socket.emit('leaveLobby', { lobbyId });
            resetState();
          }, 10000);
        }
        return updatedRanks;
      });
    });

    socket.on('playerLeft', (data) => {
      if (screen !== 'game') return;
      // Update player numbers
      const playerIndex = players.findIndex((player) => player.id === data.playerId);
      // const currentPlayerNumber = players[playerIndex].playerNumber;

      if (gameStatusRef.current !== 'in-progress') {
        const updatedPlayers = data.players.map((player, index) => ({
          ...player,
          playerNumber: index + 1,
        }));
        console.log(updatedPlayers);
        setPlayers(updatedPlayers);
      } else {
        console.log(data.players);
        setPlayers(data.players);

        console.log(ranks);

        const allCompleted = data.players?.every((player) => player.status === 'completed');

        setRanks((prevRanks) =>
          prevRanks.filter(
            (rank) => !(rank.rank === null && !players.some((player) => player.playerNumber === rank.playerNumber))
          )
        );

        console.log(ranks);

        if (allCompleted) {
          gameTimerRef.current.pause();
          socket.emit('allGamesCompleted', { lobbyId });
          setTimeout(() => {
            socket.emit('leaveLobby', { lobbyId });
            resetState();
          }, 10000);
        }
      }

      // Update currentLevels, stages, and ranks
      const newCurrentLevels = [...currentLevelsRef.current];
      const newCurrentNumbers = [...currentNumbersRef.current];
      const newStages = [...stagesRef.current];
      const newRanks = [...ranksRef.current];

      console.log(playerIndex);
      newCurrentLevels.splice(playerIndex, 1);
      newCurrentNumbers.splice(playerIndex, 1);
      newStages.splice(playerIndex, 1);
      if (players[playerIndex] && players[playerIndex].status !== 'completed') {
        newRanks.splice(playerIndex, 1);
      }

      console.log(newCurrentLevels);
      console.log(newCurrentNumbers);
      console.log(newStages);
      console.log(newRanks);

      if (gameStatusRef.current !== 'in-progress') {
        setCurrentLevels(newCurrentLevels);
        setCurrentNumbers(newCurrentNumbers);
        setStages(newStages);
      }

      // Reset state if there are no players
      if (data.players.length === 0) {
        resetGame();
      }
    });

    socket.on('stateReset', () => {
      console.log('State reset confirmed by server');
      resetState();
    });

    socket.on('connect', () => {
      if (lobbyId) {
        joinLobby(name, null, null, 'tv');
      }
    });

    socket.on('disconnect', () => {
      resetState();
    });

    return () => {
      socket.removeAllListeners();
    };
  }, [socket, lobbyId, players, ranks, screen, resetState, resetGame, joinLobby, name]);

  useEffect(() => {
    if (screen !== 'game' || gameStatus !== 'complete' || currentLevels.every((l) => l.level < 2)) return;
    gameincomplete();
  }, [currentLevels, gameStatus, gameincomplete, screen]);

  return component({
    name,
    setName,
    email,
    setEmail,
    company,
    setCompany,
    players,
    setPlayers,
    host,
    setHost,
    errorMessage,
    setErrorMessage,
    optionData,
    eventData,
    joinLobby,
    leaveLobby,
    removePlayer,
    gameStatus,
    setGameStatus,
    lobbyId,
    updatePlayerstatus,
    startGame,
    startCountdown,
    setStartCountdown,
    timeLeft,
    currentNumbers,
    setModalOpen,
    modalOpen,
    scoreTimer,
    incorrectAnswer,
    currentLevels,
    ranks,
    setLobbyId,
    stages,
    gameTimerRef,
    resetState,
    resetGame,
    index,
    setIndex,
  });
};

export default SocketWrapper;
